"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { createContext, useCallback, useContext, useMemo } from "react";

import { FALLBACK_LANGUAGE, type ResolvedLocale } from "./config";
import { localeHref, stripLocalePrefix } from "./routing";
import {
  createTranslatorFromCatalogue,
  type MessageCatalogue,
  type Translator,
} from "./translator";

/**
 * P17A-08 · The one static catalogue import in the client bundle — English
 * only, used when a request's active language IS the fallback language (the
 * common case: brand default or shopper's own choice), and as the degrade
 * catalogue for the rare render outside a `<LocaleProvider>` at all (see
 * `useTranslator` below). A non-default active language instead carries its
 * resolved catalogue down through `ClientLocale.catalogue`, computed
 * server-side by the same `getCatalogue` the RSC translator uses — so a
 * visitor never downloads a catalogue for a language they are not viewing.
 */
import enCatalogue from "../../messages/en.json";

const FALLBACK_CATALOGUE = enCatalogue as MessageCatalogue;

/**
 * P8-01B · Client-side locale context.
 *
 * Locale resolution is server-authoritative (`src/i18n/request.ts`); this just
 * carries the already-resolved value to Client Components so they can build
 * locale-correct hrefs and format on the client without a round trip. P8-04's
 * switcher reads and writes through here.
 */

/** What a client needs to know about the active locale to build links. */
export interface ClientLocale extends ResolvedLocale {
  /** The brand's default language — needed to know when a prefix is redundant. */
  defaultLanguage: string;
  /** The brand's `hideDefaultLocalePath`. */
  hideDefaultLocalePath: boolean;
  /** Languages this brand offers, for the switcher. */
  enabledLanguages: string[];
  /**
   * P8-05A · The active DISPLAY currency — what prices on this page are shown
   * in. Equals `baseCurrency` unless the shopper has switched.
   */
  currency: string;
  /** The settlement currency: what the shopper is actually charged in. */
  baseCurrency: string;
  /** Currencies offered in the switcher (always includes the base). */
  enabledCurrencies: string[];
  /**
   * True when the shopper asked for a currency we could not honour (no rate,
   * or a stale live rate) and prices fell back to base. The switcher explains
   * this rather than silently ignoring the choice.
   */
  currencyDegraded: boolean;
  /**
   * P11-19 · Per-currency usability, from the shared market read model.
   *
   * `currencyDegraded` above describes only the *selected* currency, which is
   * why the shipped switcher hid every currency the moment one rate went
   * stale. This says which individual options are choosable, so a degraded EUR
   * disables EUR and leaves USD alone.
   */
  marketCurrencies: Array<{
    code: string;
    selectable: boolean;
    health: "ok" | "no_rate" | "stale_rate";
  }>;
  /**
   * P11-19 · The "ships to" options. Empty when the brand has no regions or
   * regional pricing is off — the axis then does not render at all.
   */
  marketRegions: Array<{
    code: string;
    name: string;
    suggestedCurrency: string | null;
  }>;
  /**
   * P17A-08 · The merged catalogue for `language`, when it isn't the fallback
   * language — the client already carries `en.json` statically (see
   * `FALLBACK_CATALOGUE` above), so the root layout omits this for an
   * English-active request rather than re-sending an identical catalogue
   * through the RSC payload. Set for any other active language.
   */
  catalogue?: MessageCatalogue;
}

const LocaleContext = createContext<ClientLocale | null>(null);

export function LocaleProvider({
  value,
  children,
}: {
  value: ClientLocale;
  children: React.ReactNode;
}) {
  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

/**
 * The active locale on the client. Throws outside the provider rather than
 * returning a silent default — a component formatting money against the wrong
 * locale is a bug worth failing loudly on.
 */
export function useLocale(): ClientLocale {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useLocale must be used inside <LocaleProvider>.");
  }
  return ctx;
}

/**
 * P11-19 · The same read, for a component that may legitimately render without
 * a provider.
 *
 * Throwing is right for the common case — a price or a link silently losing its
 * locale is a bug worth failing on. It is wrong for an OPTIONAL ornament like
 * the market pill, which is now mounted in three places including the footer,
 * and whose absence should mean "render nothing" rather than "take the page
 * down". Introduced when a footer unit test, which has no reason to know about
 * i18n, started throwing because a pill appeared in the legal row.
 */
export function useOptionalLocale(): ClientLocale | null {
  return useContext(LocaleContext);
}

/**
 * Build an href that keeps the active language.
 *
 * Under `hideDefaultLocalePath` the default language yields an unprefixed path,
 * so on a single-locale brand this returns its input unchanged — which is why
 * adopting it is safe ahead of any brand actually going multi-language.
 *
 * Unlike `useLocale`, this **degrades to the identity function** when there is
 * no provider, because an unprefixed href is always a valid URL: middleware
 * rewrites it and the server resolves the locale from cookie → brand default.
 * Two places genuinely render outside the provider — `global-error.tsx`, which
 * replaces the root layout entirely, and isolated component tests — and
 * throwing in either would turn a working link into a crashed page. Silent
 * wrongness is the thing worth failing on; a link that still resolves is not.
 */
export function useLocaleHref(): (pathname: string) => string {
  const locale = useContext(LocaleContext);
  return useCallback(
    (pathname: string) =>
      locale
        ? localeHref(pathname, {
            language: locale.language,
            defaultLanguage: locale.defaultLanguage,
            hideDefault: locale.hideDefaultLocalePath,
          })
        : pathname,
    [locale],
  );
}

/**
 * A translator bound to the active language, for Client Components.
 *
 * P17A-08 · Builds from a catalogue already at hand rather than importing
 * every registered language: `locale.catalogue` when the server sent one
 * (any active language other than the fallback), else the one catalogue this
 * module keeps statically (`FALLBACK_CATALOGUE`, English) — correct because an
 * English-active request never gets a `catalogue` prop in the first place
 * (see `ClientLocale.catalogue`). The server equivalent is `getTranslator()`
 * in `./request`.
 *
 * Degrades to the same English catalogue outside a provider, for the same
 * reason `useLocaleHref` does: rendering English is recoverable, crashing is
 * not.
 */
export function useTranslator(): Translator {
  const locale = useContext(LocaleContext);
  const catalogue = locale?.catalogue ?? FALLBACK_CATALOGUE;
  return useMemo(
    () =>
      createTranslatorFromCatalogue(
        catalogue,
        locale?.language ?? FALLBACK_LANGUAGE,
      ),
    [catalogue, locale?.language],
  );
}

/**
 * The current path with any language prefix removed — `/fr/account/orders`
 * becomes `/account/orders`.
 *
 * Use this, not `usePathname()`, whenever the path is **compared against a
 * configured or hard-coded path**: nav active-state, popup targeting rules,
 * analytics grouping. Those references are all written unprefixed, so a raw
 * `usePathname()` silently stops matching the moment a shopper switches
 * language — and silently, because nothing throws: the nav just renders no
 * active item and the popup just never fires.
 *
 * Do NOT use it to rebuild the current URL (e.g. applying a new sort param).
 * That case wants the real, prefixed path — `usePathname()` is correct there.
 */
export function useCurrentPath(): string {
  return stripLocalePrefix(usePathname());
}

/**
 * `next/link` that preserves the active language. Drop-in replacement for
 * `<Link>` on internal storefront paths.
 */
export function LocaleLink({
  href,
  ...props
}: React.ComponentProps<typeof Link> & { href: string }) {
  const build = useLocaleHref();
  const localised = useMemo(() => build(href), [build, href]);
  return <Link href={localised} {...props} />;
}
